import React, { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "@mui/material";

import { Box, Stack, Typography } from "@components/base";
import { Logo } from "@components/common";

type Status = "loading" | "success" | "error";

const loadOfficeJs = (): Promise<void> =>
  new Promise((resolve, reject) => {
    if (typeof Office !== "undefined") {
      resolve();

      return;
    }

    const script = document.createElement("script");

    script.src = "https://appsforoffice.microsoft.com/lib/1/hosted/office.js";
    script.onload = (): void => resolve();
    script.onerror = (): void => reject();
    document.head.appendChild(script);
  });

const OfficeLoginPage: FC = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>("loading");

  useEffect(() => {
    const accessToken = localStorage.getItem("access_token");

    loadOfficeJs()
      .then(() => Office.onReady())
      .then(() => {
        Office.context.ui.messageParent(JSON.stringify({ accessToken }), {
          targetOrigin: import.meta.env.VITE_OFFICE_PLUGIN_URL,
        });
        setStatus("success");
      })
      .catch(() => {
        setStatus("error");
      });
  }, []);

  if (status === "loading") {
    return (
      <Stack justifyContent="center" alignItems="center" spacing={3} height="100vh">
        <Box width={160}>
          <Logo type="green0meter-with-csob" />
        </Box>
        <CircularProgress />
        <Typography variant="subtitle1" textAlign="center">
          {t("officeLogin.processing")}
        </Typography>
      </Stack>
    );
  }

  if (status === "error") {
    return (
      <Stack justifyContent="center" alignItems="center" spacing={3} height="100vh">
        <Box width={160}>
          <Logo type="green0meter-with-csob" />
        </Box>
        <Typography variant="subtitle1" textAlign="center" color="error">
          {t("officeLogin.errorNotInOffice")}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack justifyContent="center" alignItems="center" spacing={3} height="100vh">
      <Box width={160}>
        <Logo type="green0meter-with-csob" />
      </Box>
      <Typography variant="subtitle1" textAlign="center">
        {t("officeLogin.success")}
      </Typography>
    </Stack>
  );
};

export default OfficeLoginPage;
